<template>
	<layout-admin>
		<!-- Kopfzeile -->
		<header class="bg-white shadow">
			<div class="py-6 px-4 sm:px-6 lg:px-8 sm:flex sm:items-center sm:justify-between gap-x-4">
				<h1 class="text-3xl font-bold leading-tight text-gray-900 flex-shrink-0">
					{{ __("Users") }}
				</h1>

				<input
					type="search"
					v-model="search"
					:placeholder="__('Search')"
					class="block border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 w-64"
					@input="handleSearch"
				/>
			</div>
		</header>

		<admin-table
			:rows="rows"
			:cols="cols"
			:loading="loading"
			:sort-columns="sort"
			:filter-values="filters"
			:pagination="pagination"
			:row-href="rowHref"
			@sort-change="handleSortChange"
			@filter-change="handleFilterChange"
			@page-change="loadContent"
		/>
	</layout-admin>
</template>

<script>
import { debounce } from "lodash";

let options = require("../../cropOptions");

export default {
	props: {
		statusOptions: {
			type: Array,
			required: true,
		},
		typeOptions: {
			type: Array,
			required: true,
		},
		languageOptions: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			loading: true,
			rows: [],
			pagination: { currentPage: 1, lastPage: 1, total: 0 },
			sort: [],
			// Status standardmäßig komplett aktiv (alle Optionen angehakt) — inkl. DISABLED;
			// der Admin kann deaktivierte User bei Bedarf selbst abwählen.
			filters: { status: this.statusOptions.map((o) => o.id) },
			search: "",
			cols: [
				{
					id: "id",
					name: "ID",
					render: (id) => "#" + id,
				},
				{
					id: "company_name",
					name: this.__("Company"),
					tdClass: "min-w-48 max-w-xs truncate",
				},
				{
					id: "address__zip",
					name: this.__("Zip code"),
					tdClass: "min-w-24",
				},
				{
					id: "offer_buy_count",
					name: this.__("#BUY"),
				},
				{
					id: "offer_sell_count",
					name: this.__("#SELL"),
				},
				{
					id: "negotiation_count",
					name: this.__("#NEG"),
				},
				{
					id: "contract_count",
					name: this.__("#CTR"),
				},
				{
					id: "name",
					name: this.__("Contact person"),
					tdClass: "max-w-xs truncate",
				},
				{
					id: "email",
					name: this.__("E-mail"),
					tdClass: "max-w-xs truncate",
				},
				{
					id: "status",
					name: this.__("Status"),
					filterMultiple: true,
					render: (st) => {
						const option = this.statusOptions.find((o) => o.id === st);

						return option ? this.__(option.name) : this.__(st);
					},
					filterOptions: this.statusOptions.map((o) => ({
						id: o.id,
						name: this.__(o.name),
					})),
				},
				{
					id: "type",
					name: this.__("Type"),
					render: (companyType) => {
						const option = this.typeOptions.find((o) => o.id === companyType);

						return option ? this.__(option.name) : this.__("Others");
					},
					filterOptions: this.typeOptions.map((o) => ({
						id: o.id,
						name: this.__(o.name),
					})),
					filterDropdownClass:
						"origin-top-right absolute left-0 mt-2 w-80 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-500",
				},
				{
					id: "can_paper",
					name: this.__("Paper market"),
					render: (c) => (c ? this.__("Yes") : this.__("No")),
					filterOptions: [
						{ id: "1", name: this.__("Paper market") },
					],
				},
				{
					id: "subscribed",
					name: this.__("cropspot+"),
					render: (c) => (c ? this.__("Yes") : this.__("No")),
					filterOptions: [
						{ id: "1", name: this.__("cropspot+") },
					],
				},
				{
					id: "can_manage",
					name: this.__("Client boss"),
					tdClass: "min-w-48 max-w-xs truncate",
					render: (canManage, row) => {
						if (row.manager_company_name) {
							return row.manager_company_name;
						}

						return canManage ? this.__("Boss") : "";
					},
					filterOptions: [
						{ id: "1", name: this.__("Boss") },
						{ id: "0", name: this.__("client") },
					],
				},
				{
					id: "preferred_language",
					name: this.__("Language"),
					render: (lang) => {
						const option = this.languageOptions.find((o) => o.id === lang);

						return option ? this.__(option.name) : lang;
					},
					filterOptions: this.languageOptions.map((o) => ({
						id: o.id,
						name: this.__(o.name),
					})),
				},
				{
					id: "last_activity_at",
					name: this.__("Last activity"),
					render: (c) => (c ? options.formatIsoStamp(c) : null),
				},
				{
					id: "created_at",
					name: this.__("Created"),
					sortable: false,
					render: (c) => options.formatIsoStamp(c),
				},
			],
		};
	},

	mounted() {
		const page = parseInt(new URLSearchParams(window.location.search).get("page")) || 1;

		this.loadContent(page);
	},

	methods: {
		/**
		 * Lädt eine Seite der Admin-User-Daten mit aktueller Sortierung/Filter/Suche.
		 *
		 * @param {number} page
		 * @returns {void}
		 */
		loadContent(page = 1) {
			this.loading = true;

			axios
				.get(this.$route("admin.users.data"), {
					params: {
						page,
						per_page: 50,
						sort: JSON.stringify(this.sort),
						filters: JSON.stringify(this.filters),
						search: this.search || undefined,
					},
				})
				.then((response) => {
					this.rows = response.data.data;
					this.pagination.currentPage = response.data.current_page;
					this.pagination.lastPage = response.data.last_page;
					this.pagination.total = response.data.total;
					window.history.pushState(null, "", "?page=" + response.data.current_page);
				})
				.finally(() => {
					this.loading = false;
				});
		},

		handleSearch: debounce(function () {
			if (this.search.length > 0 && this.search.length < 3) {
				return;
			}

			this.loadContent(1);
		}, 400),

		/**
		 * @param {string} colId
		 * @returns {void}
		 */
		handleSortChange(colId) {
			const idx = this.sort.findIndex((s) => s.column === colId);

			if (idx === -1) {
				this.sort.push({ column: colId, dir: "asc" });
			} else if (this.sort[idx].dir === "asc") {
				this.sort.splice(idx, 1, { column: colId, dir: "desc" });
			} else {
				this.sort.splice(idx, 1);
			}

			this.loadContent(1);
		},

		/**
		 * @param {{ colId: string, value: string|null }} param
		 * @returns {void}
		 */
		handleFilterChange({ colId, value }) {
			const isEmptyArray = Array.isArray(value) && value.length === 0;

			if (value === null || isEmptyArray) {
				this.$delete(this.filters, colId);
			} else {
				this.$set(this.filters, colId, value);
			}

			this.loadContent(1);
		},

		/**
		 * @param {Object} row
		 * @returns {string}
		 */
		rowHref(row) {
			return this.$route("admin.users_show", { id: row.id });
		},
	},
};
</script>
